
import { defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter } from 'vue-router'

import ymaps from 'yandex-maps'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheTime from '@/common/components/UI/TheTime/index.vue'

// @new-components
import NewMap from '@/common/new-components/NewMap/index.vue'

// @types
import { CityT } from '@/core/types/cities.types'

// @realisations
import { cities } from '@/core/realisations/cities.realisation'
import { geocoder } from '@/core/realisations/geocoder.realisation'

// @validation
import useVuelidate from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useMap from '@/common/new-composable/useMap'
import useMapGeoObjects from '@/common/new-composable/useMapGeoObjects'
import { useDebounce } from 'vue-composable'
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'
import locationAPI from '@/new-services/location.service'
import { useMaska } from '@/common/composable/useMaska'
import { companies } from '@/core/realisations/companies.realisation'

export const WEEK_DAYS = [
    'Воскресенье',
    'Понедельник',
    'Вторник',
    'Среда',
    'Четверг',
    'Пятница',
    'Суббота',
]

export const WAREHOUSE_DEFAULT_GRAPH = [
    {
        weekday: 1,
        workTimeFrom: '09:00',
        workTimeUntil: '18:00',
    },
    {
        weekday: 2,
        workTimeFrom: '09:00',
        workTimeUntil: '18:00',
    },
    {
        weekday: 3,
        workTimeFrom: '09:00',
        workTimeUntil: '18:00',
    },
    {
        weekday: 4,
        workTimeFrom: '09:00',
        workTimeUntil: '18:00',
    },
    {
        weekday: 5,
        workTimeFrom: '09:00',
        workTimeUntil: '18:00',
    },
    {
        weekday: 6,
        workTimeFrom: '09:00',
        workTimeUntil: '18:00',
    },
    {
        weekday: 0,
        workTimeFrom: '09:00',
        workTimeUntil: '18:00',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
        NewMap,
        Loader,
        Checkbox,
        TheTime,
    },
    setup() {
        const { unmaskNumbers } = useMaska()

        let center_map_instance = null as ymaps.Map | null
        let border_map_instance = null as ymaps.Map | null

        const map_graphics = useMapGeoObjects()
        const { addGeoObject, clearMap, setMapCenter } = useMap()

        const notifications = useNotifications()
        const router = useRouter()

        const state = reactive({
            is_loading: false,
            is_center_map_ready: false,
            is_border_map_ready: false,
            //
            titleRu: null as string | null,
            titleEn: null as string | null,
            titleKz: null as string | null,
            titleZh: null as string | null,
            //
            fullNameRu: null as string | null,
            fullNameEn: null as string | null,
            fullNameKz: null as string | null,
            fullNameZh: null as string | null,
            //
            fullAddressRu: null as string | null,
            fullAddressEn: null as string | null,
            fullAddressKz: null as string | null,
            fullAddressZh: null as string | null,
            //
            streetRu: null as string | null,
            streetEn: null as string | null,
            streetKz: null as string | null,
            streetZh: null as string | null,
            //
            cityId: null as number | null,
            house: null as string | null,
            office: null as string | null,
            index: null as string | null,
            email: null as string | null,
            phone: null as string | null,
            subdivisionId: null as number | null,
            map_center: [43.25391893213578, 76.92523545812777],
            isHidden: false,
            isTerminal: 0,
            expressEnabled: 0,
            //
            schedules: [...WAREHOUSE_DEFAULT_GRAPH],
        })

        const state_form$ = useVuelidate(
            {
                cityId: { required },
                titleRu: { required },
                fullNameRu: { required },
                fullAddressRu: { required },
                streetRu: { required },
                email: { required, email },
                subdivisionId: { required },
            },
            state
        )

        const search_state = reactive({
            query: null as string | null,
            is_loading: false,
            is_failed: false,
            results: [] as {
                name: string
                street: string | null
                description?: string
                coordinates: number[]
            }[],
        })

        const data_state = reactive({
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            subdivisions: {
                is_loading: false,
                is_failed: false,
                list: [] as any[],
            },
        })

        const onBack = () => router.push('/warehouses')

        const onMapReady = (instance: ymaps.Map) => {
            center_map_instance = instance
            state.is_center_map_ready = true
            setMapCenter(center_map_instance, state.map_center)
        }

        const createPlacemark = () => {
            clearMap(center_map_instance as ymaps.Map)

            const placemark = map_graphics.createPoint(
                state.map_center,
                {
                    iconContent: 'Адрес',
                },
                {
                    preset: 'islands#blueStretchyIcon',
                }
            )

            addGeoObject(center_map_instance as ymaps.Map, placemark)
        }

        const onMapClick = (
            event: MouseEvent | ymaps.IEvent<Record<any, any>, Record<any, any>>
        ) => {
            // @ts-ignore
            const coordinates = event.get('coords')
            state.map_center = coordinates

            createPlacemark()

            setMapCenter(center_map_instance as ymaps.Map, coordinates)
            setMapCenter(border_map_instance as ymaps.Map, coordinates)
        }

        const formatCoordinatesFromString = (coords_string: string): number[] => {
            const coords = coords_string.split(' ')
            const latitude = Number(coords[1])
            const longitude = Number(coords[0])

            return [latitude, longitude]
        }

        const geocodeAddress = async () => {
            if (!search_state.query) {
                search_state.is_loading = false
                search_state.is_failed = false
                search_state.results = []
                return
            }

            try {
                search_state.is_loading = true

                let query = search_state.query
                const response = await geocoder.yandexGeocode(query, null)

                if (response.response.GeoObjectCollection.featureMember.length === 0) {
                    search_state.results = []
                    return
                }

                search_state.results = response.response.GeoObjectCollection.featureMember.map(
                    (feature_member) => {
                        const address_components =
                            feature_member.GeoObject.metaDataProperty.GeocoderMetaData.Address
                                .Components
                        const object_address_components: Record<string, string> = {}

                        address_components.forEach((component) => {
                            object_address_components[component.kind] = component.name
                        })

                        return {
                            name: feature_member.GeoObject.name,
                            street: object_address_components.street || null,
                            description: feature_member.GeoObject.description,
                            coordinates: formatCoordinatesFromString(
                                feature_member.GeoObject.Point.pos
                            ),
                        }
                    }
                )

                search_state.is_failed = false
            } catch (error) {
                console.error(error)
                search_state.is_failed = true
            } finally {
                search_state.is_loading = false
            }
        }

        const geocode = useDebounce(geocodeAddress, 1000)

        const onSelectAddress = (address: {
            name: string
            street: string | null
            description?: string
            coordinates: number[]
        }) => {
            state.map_center = address.coordinates

            createPlacemark()

            setMapCenter(center_map_instance as ymaps.Map, address.coordinates)
            setMapCenter(border_map_instance as ymaps.Map, address.coordinates)

            search_state.query = null
            search_state.results = []
        }

        const createWarehouse = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const answer = confirm('Вы уверены, что отметили адрес корректно?')
            if (!answer) return

            try {
                state.is_loading = true

                const latitude = state.map_center[0] ? state.map_center[0].toString() : null
                const longitude = state.map_center[1] ? state.map_center[1].toString() : null

                const payload: any = {
                    titleRu: state.titleRu,
                    titleEn: state.titleEn,
                    titleKz: state.titleKz,
                    titleZh: state.titleZh,
                    //
                    fullNameRu: state.fullNameRu,
                    fullNameEn: state.fullNameEn,
                    fullNameKz: state.fullNameKz,
                    fullNameZh: state.fullNameZh,
                    //
                    fullAddressRu: state.fullAddressRu,
                    fullAddressEn: state.fullAddressEn,
                    fullAddressKz: state.fullAddressKz,
                    fullAddressZh: state.fullAddressZh,
                    //
                    streetRu: state.streetRu,
                    streetEn: state.streetEn,
                    streetKz: state.streetKz,
                    streetZh: state.streetZh,
                    //
                    cityId: state.cityId,
                    house: state.house,
                    office: state.office,
                    index: state.index,
                    email: state.email,
                    phone: state.phone ? unmaskNumbers(state.phone) : null,
                    subdivisionId: state.subdivisionId,
                    latitude,
                    longitude,
                    isHidden: state.isHidden,
                    isTerminal: state.isTerminal,
                    expressEnabled: state.expressEnabled,
                    schedules: state.schedules,
                }

                await locationAPI.createWarehouse(payload)

                notifications.notify({ type: 'success', title: 'Склад добавлен' })
                onBack()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При добавлении склада произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadCities = async (page: number, search?: string) => {
            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search, 9999)
                data_state.cities.list = data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
            } finally {
                data_state.cities.is_loading = false
            }
        }

        const loadSubdivisions = async () => {
            try {
                data_state.subdivisions.is_loading = true
                const response = await companies.getSubdivisions({ page: 1 })
                data_state.subdivisions.list = response.data

                data_state.subdivisions.is_failed = false
            } catch (error) {
                data_state.subdivisions.is_failed = true
            } finally {
                data_state.subdivisions.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadCities(1)
            loadSubdivisions()
        })

        return {
            state,
            search_state,
            state_form$,
            data_state,
            onBack,
            onMapReady,
            onMapClick,
            geocode,
            onSelectAddress,
            createWarehouse,
            loadCities,
            PHONE_NUMBER_MASK,
            loadSubdivisions,
            WEEK_DAYS,
        }
    },
})
